import { SurveyType, UserProfile } from '@cpd-app/core-module/models';

import {
  AirbaseLocation,
  InstallationLocation,
  ProfileWorkType,
  ProfileWorkTypeEnum,
  SpacebaseLocation,
  UserPreGoalInput,
  UserProgramGoalInput,
  UserSchoolGoalInput
} from '@cpd-app/generated';
import { createAction, props } from '@ngrx/store';

const userProfileProp = () => props<{ userProfile: UserProfile }>();

export const loadUserProfile = createAction('[UserProfile] Load User Profile');

export const saveUserProfileToState = createAction(
  '[UserProfile] Save User Profile to State',
  userProfileProp()
);

export const userProfileLoadedSuccess = createAction(
  '[UserProfile] User Profile Loaded Success',
  userProfileProp()
);

export const userProfileLoadedFailure = createAction(
  '[UserProfile] User Profile Loaded Failure',
  props<{ error: any }>()
);

export const saveUserSkillsSurvey = createAction(
  '[UserProfile] Save User Skills Survey',
  userProfileProp()
);

export const userSkillsSurveySavedSuccess = createAction(
  '[UserProfile] User Skills Survey Saved Success',
  userProfileProp()
);

export const updateUserProfileFromMilitarySkills = createAction(
  '[UserProfile] Update User Profile From Military Skills',
  userProfileProp()
);

export const updateUserProfileFromMilitarySkillsSuccess = createAction(
  '[UserProfile] Update User Profile From Military Skills Success',
  userProfileProp()
);

export const updateUserProfileFromOtherSkills = createAction(
  '[UserProfile] Update User Profile From Other Skills',
  userProfileProp()
);

export const updateUserProfileMilitaryTransition = createAction(
  '[UserProfile] Update User Profile From Military Transition',
  // null clears the separation date (walkthrough 09-29 #19).
  props<{ monthlyTransition: number | null }>()
);

export const updateUserProfileMilitaryTransitionSuccess = createAction(
  '[UserProfile] Update User Profile From Military Transition Success',
  userProfileProp()
);

export const updateUserProfileWorkTransition = createAction(
  '[UserProfile] Update User Profile From Work Transition',
  props<{ monthlyTransition: number }>()
);

export const updateUserProfileWorkTransitionSuccess = createAction(
  '[UserProfile] Update User Profile From Work Transition Success',
  userProfileProp()
);

export const saveUserInterestsSurvey = createAction(
  '[UserProfile] Save User Interests Survey',
  userProfileProp()
);

export const userInterestsSurveySavedSuccess = createAction(
  '[UserProfile] User Interests Survey Saved Success',
  userProfileProp()
);

export const saveUserWorkValuesSurvey = createAction(
  '[UserProfile] Save User Work Values Survey',
  userProfileProp()
);

export const userWorkValuesSurveySavedSuccess = createAction(
  '[UserProfile] Save User Work Values Survey Saved Success',
  userProfileProp()
);

export const saveUserLifestyleSurvey = createAction(
  '[UserProfile] Save User Lifestyle Survey',
  userProfileProp()
);

export const userLifestyleSurveySavedSuccess = createAction(
  '[UserProfile] Save User Lifestyle Survey Saved Success',
  userProfileProp()
);

export const userProfileHasReturnLink = createAction(
  '[UserProfile] User Profile Has Return Link'
);

export const deleteUserProfile = createAction(
  '[UserProfile] Delete User Profile'
);

export const invalidateUserProfile = createAction('[UserProfile] Invalidated');

export const userCompletesAnySurvey = createAction(
  '[UserProfile] User Complete Any Survey'
);

export const saveUserPreGoals = createAction(
  '[UserProfile] Save User Pre Goals',
  props<{ values: UserPreGoalInput; surveyType: SurveyType }>()
);

export const saveUserPreGoalsSuccess = createAction(
  '[UserProfile] Save User Pre Goals Success',
  props<{ surveyType: SurveyType }>()
);

export const saveUserProgramGoals = createAction(
  '[UserProfile] Save User Program Goals',
  props<{ values: UserProgramGoalInput; surveyType: SurveyType }>()
);

export const saveUserProgramGoalsSuccess = createAction(
  '[UserProfile] Save User Program Goals Success',
  props<{ surveyType: SurveyType }>()
);
export const saveUserSchoolGoals = createAction(
  '[UserProfile] Save User School Goals',
  props<{ values: UserSchoolGoalInput; surveyType: SurveyType }>()
);

export const saveUserSchoolGoalsSuccess = createAction(
  '[UserProfile] Save User School Goals Success',
  props<{ surveyType: SurveyType }>()
);

export const initUserMatches = createAction('[UserProfile] Init User Matches');

export const initUserMatchesSuccess = createAction(
  '[UserProfile] Init User Matches Success'
);

export const acceptPrivacyPolicy = createAction(
  '[UserProfile] Accept Privacy Policy'
);

export const acceptPrivacyPolicySuccess = createAction(
  '[UserProfile] Accept Privacy Policy Success'
);
export const acceptBannerPrivacyPolicy = createAction(
  '[UserProfile] Accept Banner Privacy Policy'
);

export const acceptBannerPrivacyPolicySuccess = createAction(
  '[UserProfile] Accept Banner Privacy Policy Success'
);

export const updateProfileWorkType = createAction(
  '[UserProfile] Update Profile WorkType',
  props<{ workType: ProfileWorkTypeEnum }>()
);

export const updateProfileWorkTypeSuccess = createAction(
  '[UserProfile] Update Profile WorkType Success',
  props<{ profileWorkType: ProfileWorkType }>()
);

export const updateProfileWorkTypeFailed = createAction(
  '[UserProfile] Update Profile WorkType Failed',
  props<{ error: any }>()
);

export const saveMilitaryServeType = createAction(
  '[UserProfile] Save Military Serve Type',
  props<{ serveTypeId: number }>()
);

export const saveMilitaryServeTypeSuccess = createAction(
  '[UserProfile] Save Military Serve Type Success',
  props<{ serveTypeId: number }>()
);

export const saveMilitaryAirbaseLocation = createAction(
  '[UserProfile] Save Military Airbase Location',
  props<{ airbaseLocationId: string }>()
);

export const saveMilitaryAirbaseLocationSuccess = createAction(
  '[UserProfile] Save Military Airbase Location Success',
  props<{ userAirbaseLocation: AirbaseLocation }>()
);

export const saveMilitarySpacebaseLocation = createAction(
  '[UserProfile] Save Military Spacebase Location',
  props<{ spacebaseLocationId: string }>()
);

export const saveMilitarySpacebaseLocationSuccess = createAction(
  '[UserProfile] Save Military Spacebase Location Success',
  props<{ userSpacebaseLocation: SpacebaseLocation }>()
);

// DATA-1: all-branch installation answer, replaces the airbase/spacebase pair
// above once INSTALLATION_CAPTURE_ENABLED is on.
export const saveMilitaryInstallation = createAction(
  '[UserProfile] Save Military Installation',
  props<{ installationId: string }>()
);

export const saveMilitaryInstallationSuccess = createAction(
  '[UserProfile] Save Military Installation Success',
  props<{ userInstallation: InstallationLocation }>()
);

export const loadShareUserProfile = createAction(
  '[UserProfile] Load Share User Profile'
);

export const userShareProfileLoadedSuccess = createAction(
  '[UserProfile] User Share Profile Loaded Success',
  userProfileProp()
);

/*
 * A SAVE THAT FAILED, said out loud.
 *
 * The save effects answered a failure with `ErrorReceived` alone, which the
 * toast then suppresses for anything that looks like an authorization problem,
 * so a denied save reached the member as nothing at all: the survey advanced,
 * the step was marked done, and the answers were gone. This is dispatched
 * alongside `ErrorReceived` so a caller can tell that ITS write failed rather
 * than inferring it from a generic error.
 */
export const userSkillsSurveySavedFailure = createAction(
  '[UserProfile] User Skills Survey Saved Failure'
);

/*
 * ASVAB.9. A save of the ASVAB score set succeeded; these are the two ASVAB
 * fields of the profile the server answered with. Patches only those, so a
 * score saved in one place opens in the other two without a reload.
 */
export const asvabScoresSaved = createAction(
  '[UserProfile] ASVAB Scores Saved',
  props<{
    asvabScoreSet: { scale: string; score: number }[] | null;
    asvabScores: UserProfile['asvabScores'] | null;
  }>()
);

export const userShareProfileLoadedFailure = createAction(
  '[UserProfile] User Share Profile Loaded Failure',
  props<{ error: any }>()
);
